import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack, Speed, CheckCircle, Cancel, TimerOff, Visibility, Close } from "@mui/icons-material";
import api from "../../api/axios";
import SortableHeader from "../../components/SortableHeader";
import { useSortable } from "../../hooks/useSortable";
import { useSearchFilter } from "../../hooks/useSearchFilter";

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const CARD_ACCENTS = {
  totalNotifs: { icon: "#64748b", bg: "rgba(100,116,139,0.10)", border: "#e2e8f0" },
  accepted: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
  declined: { icon: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "#fef3c7" },
  expired: { icon: "#ef4444", bg: "rgba(220,38,38,0.10)", border: "#fee2e2" },
  acceptanceRate: { icon: "#0284c7", bg: "rgba(2,132,199,0.10)", border: "#e0f2fe" },
};

const LIMIT = 20;

export default function AgentAcceptanceRate() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allDecisions, setAllDecisions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selected, setSelected] = useState(null);

  const { sortKey, sortDir, handleSort, applySort } = useSortable();
  const { searchTerm, setSearchTerm, applyFilter } = useSearchFilter();

  const fetchPage = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      const { data } = await api.get(`/agent/notification-decisions?${params}`);
      const newDecisions = data.decisions || [];

      setAllDecisions((prev) => pageNum === 1 ? newDecisions : [...prev, ...newDecisions]);
      setPage(pageNum);
      setTotalCount(data.totalCount || 0);
      setHasMore((data.count || 0) >= LIMIT);
    } catch (err) {
      console.error("Failed to fetch notification decisions:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    fetchPage(page + 1);
  }, [page, fetchPage]);

  const sortConfig = useMemo(() => ({
    orderId: { getValue: (d) => (d.orderId || "").toLowerCase() },
    sentAt: { getValue: (d) => new Date(d.sentAt || 0).getTime() },
    vendorName: { getValue: (d) => (d.vendorName || "").toLowerCase() },
    totalAmount: { getValue: (d) => Number(d.totalAmount) || 0 },
    commission: { getValue: (d) => Number(d.commission) || 0 },
    status: { getValue: (d) => d.status || "" },
  }), []);

  const searchConfig = useMemo(() => [
    { getValue: (d) => d.orderId },
    { getValue: (d) => d.vendorName }, { getValue: (d) => d.vendorCity },
    { getValue: (d) => d.totalAmount }, { getValue: (d) => d.commission },
    { getValue: (d) => d.status }, { getValue: (d) => d.sentAt && new Date(d.sentAt).toLocaleDateString("en-IN") },
  ], []);

  const filteredDecisions = useMemo(() => applyFilter(allDecisions, searchConfig), [allDecisions, searchConfig, applyFilter]);
  const sortedDecisions = useMemo(() => applySort(filteredDecisions, sortConfig), [filteredDecisions, sortConfig, applySort]);

  const stats = useMemo(() => {
    let accepted = 0, declined = 0, expired = 0;
    allDecisions.forEach(d => {
      if (d.status === "ACCEPTED") accepted++;
      else if (d.status === "DECLINED") declined++;
      else if (d.status === "EXPIRED") expired++;
    });
    const total = accepted + declined + expired;
    const rate = total > 0 ? Math.round((accepted / total) * 100) : 0;
    return { accepted, declined, expired, total, rate };
  }, [allDecisions]);

  const kpiCards = useMemo(() => [
    { id: "totalNotifs", title: "Total Notifications", value: stats.total, progress: false },
    { id: "accepted", title: "Accepted", value: stats.accepted, progress: false },
    { id: "declined", title: "Declined", value: stats.declined, progress: false },
    { id: "expired", title: "Expired", value: stats.expired, progress: false },
    { id: "acceptanceRate", title: "Acceptance Rate", value: `${stats.rate}%`, progress: true, progressValue: stats.rate },
  ], [stats]);

  const getStatusChip = (status) => {
    if (status === "ACCEPTED") return { label: "Accepted", color: "#16a34a", bg: "rgba(22,163,74,0.12)" };
    if (status === "DECLINED") return { label: "Declined", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" };
    return { label: "Expired", color: "#ef4444", bg: "rgba(220,38,38,0.12)" };
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>📊 Acceptance Rate</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>How often you accept orders vs decline or let them expire</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Cards */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.totalNotifs;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {c.id === "accepted" ? <CheckCircle sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   c.id === "declined" ? <Cancel sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   c.id === "expired" ? <TimerOff sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   <Speed sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  {loading ? (
                    <Box sx={{ width: "70%", height: 24, bgcolor: "#e2e8f0", borderRadius: 1 }} />
                  ) : c.progress ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                      <CircularProgress variant="determinate" value={c.progressValue} size={22} thickness={5} sx={{ color: accent.icon, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }} />
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Distribution bar */}
        {!loading && stats.total > 0 && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1e293b", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Speed sx={{ fontSize: 18, color: "#0284c7" }} />
              Decision Breakdown
            </Typography>
            <Box>
              <Box sx={{ display: "flex", borderRadius: 1.5, overflow: "hidden", height: 28, mb: 1 }}>
                {stats.accepted > 0 && <Box sx={{ width: `${(stats.accepted / stats.total) * 100}%`, bgcolor: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{stats.accepted}</Typography></Box>}
                {stats.declined > 0 && <Box sx={{ width: `${(stats.declined / stats.total) * 100}%`, bgcolor: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{stats.declined}</Typography></Box>}
                {stats.expired > 0 && <Box sx={{ width: `${(stats.expired / stats.total) * 100}%`, bgcolor: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{stats.expired}</Typography></Box>}
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#16a34a" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Accepted</Typography></Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#f59e0b" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Declined</Typography></Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#ef4444" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Expired</Typography></Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Details Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
              <Speed sx={{ fontSize: 18, color: "#0284c7" }} />
              Decision Details ({allDecisions.length} of {totalCount} orders loaded)
            </Typography>
          </Box>

          <TextField size="small" placeholder="🔍 Filter orders..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mx: 2, mt: 1.5, mb: 0, "& input": { fontSize: "0.82rem" } }} />

          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={32} sx={{ color: "#0284c7", mb: 1.5 }} /><Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading decisions...</Typography></Box>
          ) : allDecisions.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(2,132,199,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><Speed sx={{ fontSize: 32, color: "#0284c7" }} /></Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No decision data</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Your acceptance decisions will appear here once you receive order notifications.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <SortableHeader label="Order" column="orderId" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Date" column="sentAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Vendor" column="vendorName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Amount" column="totalAmount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Commission" column="commission" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Decision" column="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDecisions.map((d, idx) => {
                      const chip = getStatusChip(d.status);
                      return (
                        <TableRow key={`${d.orderId}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#1e293b", fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                              #{d.orderId ? d.orderId.substring(0, 8).toUpperCase() : "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", whiteSpace: "nowrap" }}>{formatDate(d.sentAt)}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{d.vendorName || "—"}</Typography>
                            {d.vendorCity && <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", display: "block" }}>{d.vendorCity}</Typography>}
                          </TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(d.totalAmount)}</Typography></TableCell>
                          <TableCell><Typography variant="body2" sx={{ color: "#16a34a", fontSize: "0.78rem" }}>{formatCurrency(d.commission)}</Typography></TableCell>
                          <TableCell><Chip label={chip.label} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 22, bgcolor: chip.bg, color: chip.color, border: `1px solid ${chip.color}30` }} /></TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => setSelected(d)} title="View order details">
                              <Visibility sx={{ fontSize: 20, color: "#64748b" }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}>
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* ORDER DETAILS (read-only snapshot - never exposes other agent or live status) */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>Order Details</Typography>
          <IconButton onClick={() => setSelected(null)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Order #</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: "#1e293b" }}>#{selected.orderId ? selected.orderId.substring(0, 8).toUpperCase() : "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Vendor</Typography>
                <Typography variant="body1" sx={{ color: "#334155" }}>{selected.vendorName || "N/A"}</Typography>
                {selected.vendorCity && <Typography variant="caption" sx={{ color: "#94a3b8" }}>{selected.vendorCity}</Typography>}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Amount</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: "#1e293b" }}>{formatCurrency(selected.totalAmount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Commission</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: "#16a34a" }}>{formatCurrency(selected.commission)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Status</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: "#1e293b" }}>{selected.status || "N/A"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Sent On</Typography>
                  <Typography variant="body1" sx={{ color: "#334155" }}>{formatDate(selected.sentAt)}</Typography>
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block", mb: 1 }}>Items ({selected.items?.length || 0})</Typography>
                {selected.items?.length ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {selected.items.map((it, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "space-between", bgcolor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.6 }}>
                        <Typography variant="caption" sx={{ color: "#475569", fontWeight: 500 }}>{it.title || "Item"}{it.qty ? ` x${it.qty}` : ""}</Typography>
                        {it.price != null && <Typography variant="caption" fontWeight={600} sx={{ color: "#1e293b" }}>{formatCurrency(it.price)}</Typography>}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: "#94a3b8" }}>No items</Typography>
                )}
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Delivery Address</Typography>
                <Typography variant="body2" sx={{ color: "#334155" }}>
                  {selected.address ? [selected.address.addressLine1, selected.address.addressLine2].filter(Boolean).join(", ") || "N/A" : "N/A"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  {selected.address ? [selected.address.city, selected.address.state, selected.address.pincode].filter(Boolean).join(", ") : ""}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}